/**
 * Invoice refund vs bookkeeping correction (#2812).
 *
 * Svea refund returns money to the customer. Reverse payment only corrects
 * the ledger and never calls Svea. Pending / pending_manual_transfer attempts
 * are not completed customer refunds; the merchant transfer is instructions
 * only — this UI never sends the bank transfer.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import UndoIcon from '@mui/icons-material/Undo';
import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { FormDialog } from '@/components/common/FormDialog';
import { useCentsInput } from '@/hooks/useCentsInput';
import { useNotify } from '@/hooks/useNotify';
import { translateBillingError } from '@/lib/billingErrors';
import {
  classifyInvoiceRefundResponse,
  invoiceWorkOrderRef,
  isCompletedCustomerRefundStatus,
  isOpenSveaRefundStatus,
  useRefundPayment,
  useReversePayment,
  useSveaRefundAttempts,
  type InvoiceDetail,
  type SveaMerchantTransfer,
  type SveaRefundAttemptDto,
  type SveaRefundAttemptStatus,
} from '@/hooks/useInvoices';

export interface SveaPaymentOption {
  pmtId: string;
  amountCents: number | null;
}

export interface SettlementPaymentRef {
  pmtId: string;
  status: string;
  settled?: boolean;
}

const ATTEMPT_STATUS_COLOR: Record<SveaRefundAttemptStatus, 'warning' | 'info' | 'success' | 'error'> = {
  pending: 'warning',
  pending_manual_transfer: 'info',
  accepted: 'success',
  rejected: 'error',
};

const CUSTOMER_REFUND_METHODS = ['bank_transfer', 'cash', 'card', 'invoice', 'other'] as const;

const METHOD_LABEL_KEY: Record<(typeof CUSTOMER_REFUND_METHODS)[number], string> = {
  bank_transfer: 'payment.methodBankTransfer',
  cash: 'payment.methodCash',
  card: 'payment.methodCard',
  invoice: 'payment.methodInvoice',
  other: 'refund.methodOther',
};

export function mergeSveaPaymentOptions(
  request: { pmtId: string; amountCents: number; status: string } | null | undefined,
  settlementPayments: ReadonlyArray<SettlementPaymentRef> | undefined,
): SveaPaymentOption[] {
  const map = new Map<string, number | null>();
  if (request?.status === 'paid' && request.pmtId !== '') {
    map.set(request.pmtId, request.amountCents);
  }
  for (const payment of settlementPayments ?? []) {
    if (payment.pmtId !== '' && (payment.status === 'paid' || payment.settled === true)) {
      if (!map.has(payment.pmtId)) {
        map.set(payment.pmtId, null);
      }
    }
  }
  return [...map.entries()].map(([pmtId, amountCents]) => ({ pmtId, amountCents }));
}

export function consumedSveaRefundCents(
  attempts: readonly SveaRefundAttemptDto[],
  pmtId: string,
): number {
  let sum = 0;
  for (const attempt of attempts) {
    if (attempt.pmtId !== pmtId || attempt.purpose !== 'refund') continue;
    if (attempt.status === 'pending' || attempt.status === 'pending_manual_transfer' || attempt.status === 'accepted') {
      sum += attempt.amountCents;
    }
  }
  return sum;
}

export function sveaRefundableCentsForPayment(
  option: SveaPaymentOption,
  attempts: readonly SveaRefundAttemptDto[],
  paidAmountCents: number,
): number {
  const cap = option.amountCents ?? paidAmountCents;
  return Math.max(0, Math.min(paidAmountCents, cap - consumedSveaRefundCents(attempts, option.pmtId)));
}

export function nonSveaRefundableCents(
  paidAmountCents: number,
  sveaPayments: readonly SveaPaymentOption[],
  attempts: readonly SveaRefundAttemptDto[],
): number {
  const sveaPaid = sveaPayments.reduce((sum, row) => sum + (row.amountCents ?? 0), 0);
  const sveaAccepted = attempts
    .filter((row) => row.purpose === 'refund' && row.status === 'accepted')
    .reduce((sum, row) => sum + row.amountCents, 0);
  return Math.max(0, paidAmountCents - Math.max(0, sveaPaid - sveaAccepted));
}

export function isExactRefundAmount(amountCents: number, maxCents: number): boolean {
  return Number.isSafeInteger(amountCents) && amountCents > 0 && amountCents <= maxCents;
}

type RefundDialogKind = 'svea' | 'customer' | 'reverse';

interface Props {
  invoice: InvoiceDetail;
  canManage: boolean;
  sveaPayments?: ReadonlyArray<SettlementPaymentRef>;
}

export function SveaRefundControls({ invoice, canManage, sveaPayments: settlementPayments }: Props) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const refund = useRefundPayment();
  const reverse = useReversePayment();
  const attemptsQuery = useSveaRefundAttempts(
    invoice.id,
    invoice.status !== 'draft' && invoice.status !== 'void',
  );

  const attempts = attemptsQuery.data?.attempts ?? [];
  const sveaOptions = useMemo(
    () => mergeSveaPaymentOptions(invoice.sveaPaymentRequest, settlementPayments),
    [invoice.sveaPaymentRequest, settlementPayments],
  );

  const [dialog, setDialog] = useState<RefundDialogKind | null>(null);
  const [amountCents, setAmountCents] = useState(0);
  const [reason, setReason] = useState('');
  const [pmtId, setPmtId] = useState('');
  const [method, setMethod] = useState<(typeof CUSTOMER_REFUND_METHODS)[number]>('bank_transfer');
  const [manualTransfer, setManualTransfer] = useState<SveaMerchantTransfer | null>(null);
  const amountInput = useCentsInput(amountCents, setAmountCents, dialog ?? 'closed');

  const selectedSvea = sveaOptions.find((row) => row.pmtId === pmtId) ?? null;
  const sveaMax = selectedSvea
    ? sveaRefundableCentsForPayment(selectedSvea, attempts, invoice.paidAmountCents)
    : 0;
  const customerMax = nonSveaRefundableCents(invoice.paidAmountCents, sveaOptions, attempts);
  const reverseMax = Math.max(0, invoice.paidAmountCents);
  const dialogMax = dialog === 'svea' ? sveaMax : dialog === 'customer' ? customerMax : reverseMax;
  const amountValid = isExactRefundAmount(amountCents, dialogMax);
  const sveaNeedsExplicitPmt = sveaOptions.length > 1;
  const sveaPmtReady = dialog !== 'svea' || pmtId !== '';

  const showSveaAction = canManage && sveaOptions.length > 0 && invoice.paidAmountCents > 0;
  const showCustomerAction = canManage && customerMax > 0;
  const showReverseAction = canManage && reverseMax > 0;
  const showAttempts = attempts.length > 0 || manualTransfer !== null;
  const showSection =
    invoice.status !== 'void' &&
    invoice.status !== 'draft' &&
    (showSveaAction || showCustomerAction || showReverseAction || showAttempts);

  if (!showSection) return null;

  const openDialog = (kind: RefundDialogKind) => {
    setManualTransfer(null);
    setReason('');
    setMethod('bank_transfer');
    if (kind === 'svea') {
      const only = sveaOptions.length === 1 ? sveaOptions[0] : undefined;
      const nextPmt = only?.pmtId ?? '';
      setPmtId(nextPmt);
      const max = only
        ? sveaRefundableCentsForPayment(only, attempts, invoice.paidAmountCents)
        : 0;
      setAmountCents(max);
    } else if (kind === 'customer') {
      setPmtId('');
      setAmountCents(customerMax);
    } else {
      setPmtId('');
      setAmountCents(reverseMax);
    }
    setDialog(kind);
  };

  const closeDialog = () => {
    setDialog(null);
    setReason('');
    setPmtId('');
  };

  const handleSveaPmtChange = (nextPmtId: string) => {
    setPmtId(nextPmtId);
    const option = sveaOptions.find((row) => row.pmtId === nextPmtId);
    if (!option) return;
    const max = sveaRefundableCentsForPayment(option, attempts, invoice.paidAmountCents);
    setAmountCents(max);
  };

  const handleRefund = async (kind: 'svea' | 'customer') => {
    if (!amountValid || (kind === 'svea' && !sveaPmtReady)) return;
    try {
      const result = await refund.mutateAsync({
        invoiceId: invoice.id,
        amountCents,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(kind === 'svea' ? { pmtId } : { method }),
        ...invoiceWorkOrderRef(invoice.bookingId),
      });
      const outcome = classifyInvoiceRefundResponse(result);
      if (outcome === 'pending_manual_transfer') {
        const transfer = result.merchantTransfer ?? result.sveaRefundAttempt?.merchantTransfer ?? null;
        setManualTransfer(transfer);
        notify.warning(t('refund.pendingManualTitle'), t('refund.pendingManualHelp'));
      } else if (outcome === 'pending') {
        setManualTransfer(null);
        notify.warning(t('refund.pendingTitle'), t('refund.pendingHelp'));
      } else {
        setManualTransfer(null);
        notify.success(kind === 'svea' ? t('refund.sveaSuccess') : t('refund.customerSuccess'));
      }
      closeDialog();
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleReverse = async () => {
    if (!amountValid) return;
    try {
      await reverse.mutateAsync({
        invoiceId: invoice.id,
        amountCents,
        ...(reason.trim() ? { note: reason.trim() } : {}),
        ...invoiceWorkOrderRef(invoice.bookingId),
      });
      notify.success(t('refund.reverseSuccess'));
      closeDialog();
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} data-testid="svea-refund-controls">
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {t('refund.actionsLabel')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('refund.sveaHelp')}
      </Typography>

      {canManage ? (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: showAttempts ? 2 : 0 }}
          aria-label={t('refund.actionsLabel')}
        >
          {showSveaAction ? (
            <Button
              variant="outlined"
              startIcon={<AccountBalanceIcon />}
              onClick={() => openDialog('svea')}
              data-testid="svea-refund-svea-button"
              aria-label={t('refund.sveaAction')}
            >
              {t('refund.sveaAction')}
            </Button>
          ) : null}
          {showCustomerAction ? (
            <Button
              variant="outlined"
              startIcon={<PaymentsIcon />}
              onClick={() => openDialog('customer')}
              data-testid="svea-refund-customer-button"
              aria-label={t('refund.customerAction')}
            >
              {t('refund.customerAction')}
            </Button>
          ) : null}
          {showReverseAction ? (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<UndoIcon />}
              onClick={() => openDialog('reverse')}
              data-testid="svea-refund-reverse-button"
              aria-label={t('refund.reverseAction')}
            >
              {t('refund.reverseAction')}
            </Button>
          ) : null}
        </Stack>
      ) : null}

      {manualTransfer ? <MerchantTransferPanel transfer={manualTransfer} /> : null}

      {attempts.length > 0 ? (
        <Box data-testid="svea-refund-attempts-list">
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('refund.attemptsTitle')}
          </Typography>
          <Stack spacing={1.5}>
            {attempts.map((attempt) => (
              <AttemptCard key={attempt.id} attempt={attempt} />
            ))}
          </Stack>
        </Box>
      ) : null}

      <FormDialog
        open={dialog === 'svea'}
        onClose={closeDialog}
        title={t('refund.sveaTitle')}
        isDirty={amountCents > 0 || reason !== '' || pmtId !== ''}
        headerIcon={<AccountBalanceIcon />}
        onSubmit={() => void handleRefund('svea')}
        submitLabel={t('refund.sveaSubmit')}
        isLoading={refund.isPending}
        submitDisabled={!amountValid || !sveaPmtReady}
        maxWidth="sm"
      >
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="svea-refund-dialog">
            <Typography variant="body2" color="text.secondary">
              {t('refund.sveaHelp')}
            </Typography>
            {sveaNeedsExplicitPmt || sveaOptions.length > 0 ? (
              <FormControl fullWidth size="small" required={sveaNeedsExplicitPmt}>
                <InputLabel id="svea-refund-pmt-label">{t('refund.pmtId')}</InputLabel>
                <Select
                  labelId="svea-refund-pmt-label"
                  label={t('refund.pmtId')}
                  value={pmtId}
                  onChange={(event) => handleSveaPmtChange(String(event.target.value))}
                  data-testid="svea-refund-pmt-select"
                  inputProps={{ 'aria-label': t('refund.pmtId') }}
                >
                  {sveaNeedsExplicitPmt ? (
                    <MenuItem value="">
                      <em>{t('refund.pmtIdRequired')}</em>
                    </MenuItem>
                  ) : null}
                  {sveaOptions.map((option) => (
                    <MenuItem key={option.pmtId} value={option.pmtId}>
                      {option.pmtId}
                      {option.amountCents != null ? ` · ${formatPrice(option.amountCents)}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            {sveaNeedsExplicitPmt && pmtId === '' ? (
              <Alert severity="info">{t('refund.pmtIdRequired')}</Alert>
            ) : null}
            <AmountReasonFields
              amountInput={amountInput}
              amountCents={amountCents}
              maxCents={dialogMax}
              amountValid={amountValid}
              reason={reason}
              onReason={setReason}
              reasonLabel={t('refund.reason')}
            />
          </Stack>
        </DialogContent>
      </FormDialog>

      <FormDialog
        open={dialog === 'customer'}
        onClose={closeDialog}
        title={t('refund.customerTitle')}
        isDirty={amountCents > 0 || reason !== ''}
        headerIcon={<PaymentsIcon />}
        onSubmit={() => void handleRefund('customer')}
        submitLabel={t('refund.customerSubmit')}
        isLoading={refund.isPending}
        submitDisabled={!amountValid}
        maxWidth="sm"
      >
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="svea-refund-customer-dialog">
            <Typography variant="body2" color="text.secondary">
              {t('refund.customerHelp')}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel id="svea-refund-method-label">{t('refund.method')}</InputLabel>
              <Select
                labelId="svea-refund-method-label"
                label={t('refund.method')}
                value={method}
                onChange={(event) =>
                  setMethod(event.target.value as (typeof CUSTOMER_REFUND_METHODS)[number])
                }
                inputProps={{ 'aria-label': t('refund.method') }}
                data-testid="svea-refund-method-select"
              >
                {CUSTOMER_REFUND_METHODS.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(METHOD_LABEL_KEY[value])}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <AmountReasonFields
              amountInput={amountInput}
              amountCents={amountCents}
              maxCents={dialogMax}
              amountValid={amountValid}
              reason={reason}
              onReason={setReason}
              reasonLabel={t('refund.reason')}
            />
          </Stack>
        </DialogContent>
      </FormDialog>

      <FormDialog
        open={dialog === 'reverse'}
        onClose={closeDialog}
        title={t('refund.reverseTitle')}
        isDirty={amountCents > 0 || reason !== ''}
        headerIcon={<UndoIcon />}
        onSubmit={() => void handleReverse()}
        submitLabel={t('refund.reverseSubmit')}
        submitColor="warning"
        isLoading={reverse.isPending}
        submitDisabled={!amountValid}
        maxWidth="sm"
      >
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }} data-testid="svea-refund-reverse-dialog">
            <Alert severity="warning">{t('refund.reverseHelp')}</Alert>
            <AmountReasonFields
              amountInput={amountInput}
              amountCents={amountCents}
              maxCents={dialogMax}
              amountValid={amountValid}
              reason={reason}
              onReason={setReason}
              reasonLabel={t('refund.reverseNote')}
            />
          </Stack>
        </DialogContent>
      </FormDialog>
    </Paper>
  );
}

function AmountReasonFields({
  amountInput,
  amountCents,
  maxCents,
  amountValid,
  reason,
  onReason,
  reasonLabel,
}: {
  amountInput: ReturnType<typeof useCentsInput>;
  amountCents: number;
  maxCents: number;
  amountValid: boolean;
  reason: string;
  onReason: (value: string) => void;
  reasonLabel: string;
}) {
  const { t } = useTranslation('invoices');
  const showError = amountCents !== 0 && !amountValid;
  return (
    <>
      <TextField
        size="small"
        label={t('refund.amount')}
        value={amountInput.value}
        onChange={amountInput.onChange}
        onFocus={amountInput.onFocus}
        onBlur={amountInput.onBlur}
        error={showError}
        helperText={
          showError
            ? t('refund.amountInvalid')
            : t('refund.amountHint', { max: formatPrice(maxCents) })
        }
        data-testid="svea-refund-amount"
        slotProps={{ htmlInput: { 'aria-label': t('refund.amount'), inputMode: 'decimal' } }}
      />
      <TextField
        size="small"
        label={reasonLabel}
        value={reason}
        onChange={(event) => onReason(event.target.value)}
        multiline
        rows={2}
        slotProps={{ htmlInput: { 'aria-label': reasonLabel } }}
      />
    </>
  );
}

function AttemptCard({ attempt }: { attempt: SveaRefundAttemptDto }) {
  const { t } = useTranslation('invoices');
  const open = isOpenSveaRefundStatus(attempt.status);
  const completed = isCompletedCustomerRefundStatus(attempt.status);
  const transfer = attempt.merchantTransfer;
  return (
    <Box
      sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
      data-testid={`svea-refund-attempt-${attempt.id}`}
      data-attempt-status={attempt.status}
      data-completed-customer-refund={completed ? 'true' : 'false'}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={ATTEMPT_STATUS_COLOR[attempt.status]}
          label={t(`refund.status.${attempt.status}`)}
          data-testid={`svea-refund-attempt-status-${attempt.id}`}
        />
        <Typography variant="body2">{t(`refund.purpose.${attempt.purpose}`)}</Typography>
        <Typography variant="body2" fontWeight={600}>
          {formatPrice(attempt.amountCents)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('refund.pmtId')}: {attempt.pmtId}
        </Typography>
      </Stack>
      {attempt.status === 'pending' ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('refund.pendingHelp')}
        </Typography>
      ) : null}
      {attempt.status === 'pending_manual_transfer' && transfer ? (
        <MerchantTransferPanel transfer={transfer} />
      ) : null}
      {open ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('refund.pendingTitle')}
        </Typography>
      ) : null}
    </Box>
  );
}

function MerchantTransferPanel({ transfer }: { transfer: SveaMerchantTransfer }) {
  const { t } = useTranslation('invoices');
  return (
    <Alert
      severity="info"
      sx={{ mt: 1 }}
      data-testid="svea-refund-transfer-instructions"
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {t('refund.pendingManualTitle')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('refund.pendingManualHelp')}
      </Typography>
      <Stack spacing={0.5}>
        <TransferRow label={t('refund.transferRecipient')} value={transfer.recipientName} />
        <TransferRow label={t('refund.transferIban')} value={transfer.iban} />
        <TransferRow label={t('refund.transferReference')} value={transfer.reference} />
        <TransferRow label={t('refund.transferAmount')} value={formatPrice(transfer.amountCents)} />
      </Stack>
      <Typography variant="body2" sx={{ mt: 1 }}>
        {t('refund.noTransferAction')}
      </Typography>
    </Alert>
  );
}

function TransferRow({ label, value }: { label: string; value: string }) {
  return (
    <Typography variant="body2">
      <Box component="span" sx={{ fontWeight: 600 }}>{label}: </Box>
      {value}
    </Typography>
  );
}
